import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';
import {useRouter} from 'next/router';
import {Button} from 'components/Button';
import {Input} from 'components/Input';
import Icon from 'components/Icon';
import {FilterCheckbox} from 'plugins/modules/filter/components/Checkbox';
import {Radio} from 'plugins/modules/filter/components/Radio';
import {MetroStation} from 'modules/wineshops-list/components/MetroStation';
import {useIsScrollable} from 'core/hooks/useIsScrollable';
import {parseEventsFiltersFromUrl} from 'modules/events/helpers/filters';
import {EventFiltersValuesJoinType, EVENTS_FILTERS_CONFIG} from 'modules/events/constants';
import {IFilterEntry, TEventsFilterValue} from 'modules/events/types';
import style from 'modules/events/components/EventFilter/components/style.module.scss';
import {Typography} from 'components/Typography';
import {useTouchMoveDisable} from 'core/hooks/useTouchMoveDisable';

interface IProps {
    filterTitle: (typeof EVENTS_FILTERS_CONFIG)[number]['title'];
    filterJoinType?: EventFiltersValuesJoinType;
    withSearch?: boolean;
    filterCode: IFilterEntry['filterCode'];
    filterValues: TEventsFilterValue[];
    onClose?: () => void;
}

const EventFilterPopup: FC<IProps> = ({
    filterTitle,
    filterJoinType = EventFiltersValuesJoinType.OR,
    withSearch = false,
    filterCode,
    filterValues,
    onClose,
}) => {
    useTouchMoveDisable();
    const router = useRouter();

    const [ref, isScrollable] = useIsScrollable<HTMLDivElement>();

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    useEffect(() => {
        const filters = parseEventsFiltersFromUrl(router.asPath);
        const currentFilter = filters.find((filter) => filter.code === filterCode);
        setSelectedValues(currentFilter?.values || []);
    }, [router.asPath, filterCode]);

    const filteredValues = useMemo(() => {
        if (!searchQuery.trim()) {
            return filterValues;
        }

        return filterValues.filter((filterValue) => {
            return filterValue.title.toLowerCase().includes(searchQuery.toLowerCase());
        });
    }, [filterValues, searchQuery]);

    const handleApplyFilter = useCallback(
        async (queryData: Record<string, string>) => {
            const {pathname, query} = router;
            const {page, ...restParams} = query;

            try {
                await router.push({pathname, query: {...restParams, ...queryData}}, undefined, {shallow: true});
            } catch {}
        },
        [router],
    );

    const handleApplyClick = useCallback(async () => {
        const {pathname, query: currentQuery} = router;
        const {page, ...restParams} = currentQuery;

        try {
            if (0 === selectedValues.length) {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const {[filterCode]: _, ...newQuery} = restParams;
                await router.push({pathname, query: newQuery}, undefined, {shallow: true});
            } else {
                const filterQuery = {
                    [filterCode]:
                        filterJoinType === EventFiltersValuesJoinType.OR
                            ? selectedValues.join('-or-')
                            : selectedValues[0],
                };
                await handleApplyFilter(filterQuery);
            }

            onClose?.();
        } catch {}
    }, [handleApplyFilter, filterCode, selectedValues, router, filterJoinType, onClose]);

    const handleValueChange = useCallback(
        (value: string, isChecked: boolean) => {
            setSelectedValues((prevValues) => {
                if (filterJoinType === EventFiltersValuesJoinType.OR) {
                    return isChecked ? [...prevValues, value] : prevValues.filter((v) => v !== value);
                }
                return isChecked ? [value] : prevValues.filter((v) => v !== value);
            });
        },
        [filterJoinType],
    );

    const renderFilterItem = (filterValue: TEventsFilterValue) => {
        const isWinery = 'metro' in filterValue;
        let value: string;
        if (isWinery) {
            value = filterValue.id;
        } else if ('code' in filterValue) {
            value = String(filterValue.code);
        } else {
            value = filterValue.title;
        }

        const {title, count} = filterValue;

        const isChecked = selectedValues.includes(value);

        return (
            <div key={title} className={style.filterListItem}>
                <div className={style.title}>
                    {filterJoinType === EventFiltersValuesJoinType.OR ? (
                        <FilterCheckbox
                            code={filterCode}
                            count={count}
                            isDefaultChecked={isChecked}
                            onChange={(_, checked) => handleValueChange(value, checked)}
                            title={title}
                            description={
                                isWinery &&
                                0 < filterValue.metro.length && (
                                    <div className={style.metro}>
                                        {filterValue.metro.map((metro, index) => (
                                            <MetroStation
                                                key={`${filterValue.title}-${metro.name}-${index}`}
                                                name={metro.name}
                                                hexColor={metro.color}
                                            />
                                        ))}
                                    </div>
                                )
                            }
                            className={isWinery ? style.wineryCheckbox : ''}
                        />
                    ) : (
                        <Radio
                            isChecked={isChecked}
                            name={filterCode}
                            onChange={() => handleValueChange(value, !isChecked)}
                            title={title}
                            count={count}
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={style.popupContent}>
            <Typography variant={'title-20-m'} className={style.popupTitle}>
                {filterTitle}
            </Typography>
            {filterJoinType === EventFiltersValuesJoinType.OR && withSearch && (
                <div className={style.popupSearch}>
                    <Input
                        placeholder={'Быстрый поиск'}
                        trailIcon={<Icon icon={'search'} isAutoSize={true} />}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            )}

            <div
                ref={ref}
                className={classNames(style.popupFiltersList, {
                    [style.isScrollable]: isScrollable,
                })}
            >
                {filteredValues.map(renderFilterItem)}
            </div>

            <div className={style.filterApplyButton}>
                <Button size={'medium'} theme={'primary'} onClick={handleApplyClick}>
                    Применить
                </Button>
            </div>
        </div>
    );
};

export default EventFilterPopup;
